import { supabase } from '../config/supabaseClient';

async function invokeMarkOrderStatus(orderId, status, reason) {
  const { data, error } = await supabase.functions.invoke('mark-order-status', {
    body: { orderId, status, reason: reason || undefined },
  });
  if (error) throw new Error(error.message || `Failed to mark order as ${status}`);
  if (data?.error) throw new Error(data.error);
  return data;
}

export const FREE_SHIPPING_THRESHOLD = 499;
export const SHIPPING_FEE = 49;

export function generateOrderNumber() {
  const date = new Date();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `MM${y}${m}${d}${rand}`;
}

export function computeShipping(subtotal) {
  return subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
}

export function computeOrderTotals(subtotal, discountAmount = 0) {
  const shipping = computeShipping(subtotal);
  const total = Math.max(0, subtotal - discountAmount + shipping);
  return { subtotal, discountAmount, shipping, total };
}

export async function createOrder({
  firebaseUid,
  profileId,
  items,
  subtotal,
  discountAmount,
  shippingAmount,
  totalAmount,
  couponCode,
  couponId,
  shipping,
  customerEmail,
}) {
  const orderNumber = generateOrderNumber();

  const { data: order, error: orderError } = await supabase
    .from('orders')
    .insert({
      order_number: orderNumber,
      firebase_uid: firebaseUid,
      profile_id: null,  // We use firebase_uid for order-user association
      status: 'pending',
      subtotal,
      discount_amount: discountAmount,
      shipping_amount: shippingAmount,
      total_amount: totalAmount,
      coupon_code: couponCode || null,
      coupon_id: couponId || null,
      shipping_name: shipping.name,
      shipping_phone: shipping.phone,
      shipping_address: shipping.address,
      shipping_city: shipping.city,
      shipping_state: shipping.state,
      shipping_pincode: shipping.pincode,
      shipping_email: shipping.email || null,
    })
    .select()
    .single();

  if (orderError) throw orderError;

  const orderItems = items.map((item) => ({
    order_id: order.id,
    product_id: item.id,
    product_name: item.name,
    product_size: item.size || null,
    product_image: item.image || null,
    quantity: item.qty,
    unit_price: item.price,
    line_total: item.price * item.qty,
  }));

  const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
  if (itemsError) throw itemsError;

  return order;
}

// NOTE: markOrderPaid is deprecated - payment marking is handled server-side
// by the verify-razorpay-payment Edge Function, which uses the service_role key
// and verifies the Razorpay signature. DO NOT call this from client code.
export async function markOrderPaid(orderId, paymentDetails) {
  throw new Error(
    'markOrderPaid is deprecated. Payment status is verified server-side ' +
    'via verify-razorpay-payment Edge Function. Do not call this directly.'
  );
}

export async function markOrderFailed(orderId, reason) {
  const result = await invokeMarkOrderStatus(orderId, 'failed', reason);
  return result.order;
}

export async function fetchOrderByNumber(orderNumber, phone) {
  const normalizedPhone = phone.replace(/\D/g, '').slice(-10);

  const { data: order, error } = await supabase
    .from('orders')
    .select('*')
    .eq('order_number', orderNumber.trim().toUpperCase())
    .maybeSingle();

  if (error) throw error;
  if (!order) return null;

  const orderPhone = (order.shipping_phone || '').replace(/\D/g, '').slice(-10);
  if (orderPhone !== normalizedPhone) return null;

  const { data: items, error: itemsError } = await supabase
    .from('order_items')
    .select('*')
    .eq('order_id', order.id);

  if (itemsError) throw itemsError;

  return { ...order, items: items || [] };
}

export async function fetchOrdersByUser(firebaseUid) {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('firebase_uid', firebaseUid)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function fetchOrderById(orderId) {
  const { data: order, error } = await supabase
    .from('orders')
    .select('*')
    .eq('id', orderId)
    .maybeSingle();

  if (error) throw error;
  if (!order) return null;

  const { data: items, error: itemsError } = await supabase
    .from('order_items')
    .select('*')
    .eq('order_id', order.id);

  if (itemsError) throw itemsError;

  return { ...order, items: items || [] };
}
